// src/pos-app/surfaces/cashier/TableSelect.js
//
// F12a-N Task 4 — the table-selection region of the native Sell surface
// (design contract: docs/mockups/f12a-n/cashier-sell.html, State 1).
// Three variants, driven by the selected dining option's tables config:
//   - List: numbered status tiles (left status bar, dot+label, meta line,
//     merged "Group · N" badge) + 5-status legend + List|Visual toggle.
//   - Visual: the REUSED <VisualTableSelector/> in hideHeader mode (fills
//     the slot, scales the 800×500 canvas to fit — no page scroll; zero
//     edits to the component). Click resolution is delegated back here so
//     both views share ONE selection handler.
//   - Random number ('random_number' config): entry strip + the
//     active-orders quick grid (legacy CashierSalesPage.js:2641-2690).
//
// Status derivation lives in ONE exported helper (deriveTableStatus) that
// mirrors VisualTableSelector.getTableStatus exactly, so the List tiles
// always agree with the reused floor-plan canvas (the legacy page carries
// its own inline copy at 2716-2731 — this file is the native surface's
// single source). Merge resolution reuses the exported buildTableGroups /
// resolveOccupation from VisualTableSelector — never re-derived.
//
// Selection semantics are faithful to the legacy list-tile click
// (CashierSalesPage.js:2749-2762): merged members resolve to their anchor;
// a table whose anchor key has a pending order opens that order via
// onOpenOrder (Task 6 wires the real detail modal); otherwise the table is
// selected and SellSurface's mode gate flips to browse. The legacy
// editing-conflict guard arrives with Task 6 (no editing state exists yet).
import React, { useMemo } from 'react';
import { useLanguage, getEntityTranslation, translateDynamic } from '../../../context/LanguageContext';
import VisualTableSelector, { buildTableGroups, resolveOccupation } from '../../../components/pos/VisualTableSelector';

// Status → theme token. Same five states (and the same hues) as
// VisualTableSelector.STATUS_COLORS, expressed as themeable vars so the
// list view follows light/dark.
export const STATUS_TOKENS = {
    available: 'var(--c-success)',
    seated: 'var(--c-blue)',
    ordered: 'var(--c-red)',
    awaiting_bill: 'var(--c-amber)',
    paid: 'var(--c-mutedLight)',
};

// The ONE status-derivation helper for the native surface — semantics are
// exactly VisualTableSelector.getTableStatus (minus its 'selected' state,
// which cannot occur here: selecting a table leaves the grid). `occ` must
// already be merge-resolved via resolveOccupation.
export const deriveTableStatus = (occ, hasPendingOrder) => {
    if (occ) {
        if (occ.paid_at && !occ.cleared_at) return 'paid';
        if (occ.bill_requested_at && !occ.paid_at) return 'awaiting_bill';
        if (occ.status === 'active') {
            return (hasPendingOrder || occ.first_order_at) ? 'ordered' : 'seated';
        }
    }
    return hasPendingOrder ? 'ordered' : 'available';
};

// Tokens only (--c-* theme vars, light + dark). Logical properties keep the
// region RTL-correct; every tap target is ≥44px (tiles are ≥52px tall, the
// 36px seg buttons and 34px confirm get ::after hit-area extensions).
const CSS = `
.tsl-root{flex:1;min-height:0;display:flex;flex-direction:column;overflow:hidden;}

/* header row — mockup .sect-row (title+sub, seg toggle, legend) */
.tsl-head{flex:none;display:flex;align-items:center;gap:12px;margin-bottom:12px;flex-wrap:wrap;}
.tsl-title{font-size:17px;font-weight:800;letter-spacing:-.2px;color:var(--c-ink);}
.tsl-sub{font-size:12.5px;color:var(--c-muted);font-weight:600;}

/* List|Visual segmented toggle — mockup .seg */
.tsl-seg{display:inline-flex;background:var(--c-faint);border:1px solid var(--c-border);border-radius:12px;padding:3px;gap:2px;}
.tsl-seg button{position:relative;height:36px;padding:0 16px;border:none;border-radius:9px;cursor:pointer;
  font-family:inherit;font-size:12.5px;font-weight:700;color:var(--c-muted);background:none;
  display:inline-flex;align-items:center;gap:6px;}
.tsl-seg button::after{content:"";position:absolute;inset-block:-5px;inset-inline:0;}
.tsl-seg button.active{background:var(--c-surface);color:var(--c-green);box-shadow:0 1px 4px rgba(15,23,42,.12);}

/* 5-status legend — mockup .legend */
.tsl-legend{display:flex;gap:14px;flex-wrap:wrap;margin-inline-start:auto;}
.tsl-leg{display:inline-flex;align-items:center;gap:6px;font-size:11.5px;font-weight:700;
  text-transform:uppercase;letter-spacing:.4px;}
.tsl-leg i{width:9px;height:9px;border-radius:50%;background:currentColor;}

/* numbered table grid — the ONLY scroller in list mode (mockup .tables/.ttile) */
.tsl-grid{flex:1;min-height:0;overflow-y:auto;align-content:start;
  display:grid;grid-template-columns:repeat(auto-fill,minmax(158px,1fr));gap:10px;padding-bottom:6px;}
.tsl-tile{position:relative;min-height:96px;padding:12px 14px;border-radius:14px;text-align:start;cursor:pointer;
  font-family:inherit;background:var(--c-surface);border:1px solid var(--c-border);box-shadow:0 1px 3px rgba(15,23,42,.06);
  display:flex;flex-direction:column;justify-content:space-between;gap:6px;overflow:hidden;
  transition:transform .12s, box-shadow .12s, border-color .12s;}
.tsl-tile::before{content:"";position:absolute;inset-inline-start:0;top:0;bottom:0;width:4px;background:var(--tt,var(--c-success));}
.tsl-tile[data-status="available"]:hover{border-color:var(--c-greenBorder);transform:translateY(-1px);
  box-shadow:0 4px 14px color-mix(in srgb, var(--c-success) 18%, transparent);}
.tsl-tile.busy:hover{border-color:var(--c-borderHover);}
.tsl-num{font-size:19px;font-weight:800;letter-spacing:-.3px;color:var(--c-ink);}
.tsl-stat{display:inline-flex;align-items:center;gap:6px;font-size:11.5px;font-weight:700;}
.tsl-stat i{width:8px;height:8px;border-radius:50%;background:currentColor;flex:none;}
.tsl-meta{display:block;margin-top:2px;font-size:11.5px;color:var(--c-muted);font-weight:600;
  font-variant-numeric:tabular-nums;}
.tsl-merged{position:absolute;top:10px;inset-inline-end:10px;font-size:10px;font-weight:800;letter-spacing:.4px;
  padding:3px 8px;border-radius:999px;background:var(--c-greenLight);color:var(--c-green);border:1px solid var(--c-greenBorder);
  font-variant-numeric:tabular-nums;}
.tsl-none{grid-column:1/-1;text-align:center;padding:24px 0;font-size:13px;font-weight:600;color:var(--c-muted);}

/* visual mode — bounded slot; the reused selector scales its canvas to fit */
.tsl-canvas{flex:1;min-height:0;position:relative;overflow:hidden;border-radius:14px;}

/* random-number entry strip — mockup .orderno-strip */
.tsl-entry{flex:none;display:flex;align-items:center;gap:10px;padding:9px 13px;margin-bottom:12px;border-radius:12px;
  background:var(--c-greenLight);border:1px solid var(--c-greenBorder);font-size:12.5px;font-weight:700;color:var(--c-slate);}
.tsl-entry input{width:90px;height:34px;border:1px solid var(--c-greenBorder);border-radius:9px;background:var(--c-surface);
  color:var(--c-ink);text-align:center;font-family:inherit;font-weight:800;font-size:14px;outline:none;
  font-variant-numeric:tabular-nums;}
.tsl-entry input:focus{border-color:var(--c-green);}
.tsl-confirm{position:relative;height:34px;padding:0 14px;border:none;border-radius:9px;cursor:pointer;
  font-family:inherit;font-size:12.5px;font-weight:700;background:var(--c-green);color:#fff;}
.tsl-confirm::after{content:"";position:absolute;inset:-6px;}
.tsl-confirm:disabled{background:var(--c-border);color:var(--c-muted);cursor:not-allowed;}

/* active-orders quick grid (random mode) — legacy 2666-2687 */
.tsl-active-h{flex:none;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;
  color:var(--c-mutedLight);margin-bottom:8px;}
.tsl-active-grid{flex:1;min-height:0;overflow-y:auto;align-content:start;
  display:grid;grid-template-columns:repeat(auto-fill,minmax(96px,1fr));gap:10px;padding-bottom:6px;}
.tsl-active-tile{min-height:52px;border-radius:12px;cursor:pointer;font-family:inherit;
  background:var(--c-amberLight);border:1px solid var(--c-amberBorder);color:var(--c-amber);
  font-size:14px;font-weight:800;font-variant-numeric:tabular-nums;
  display:flex;align-items:center;justify-content:center;
  transition:transform .12s, box-shadow .12s;}
.tsl-active-tile:hover{transform:translateY(-1px);box-shadow:0 4px 12px color-mix(in srgb, var(--c-amber) 22%, transparent);}
`;

const ListIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <path d="M8 6h13M8 12h13M8 18h13" />
        <circle cx="4" cy="6" r="1" fill="currentColor" /><circle cx="4" cy="12" r="1" fill="currentColor" /><circle cx="4" cy="18" r="1" fill="currentColor" />
    </svg>
);

const GridIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" />
    </svg>
);

// Local wall-clock time (never UTC) for tile meta lines.
const fmtTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export default function TableSelect({
    selectedDiningOption,
    diningOptionObjects,
    fullDiningOptions,
    availableTableNumbers,
    tableNumber,
    setTableNumber,
    setTableNumberConfirmed,
    viewMode,
    setViewMode,
    pendingOrders,
    printedOrders,
    occupations,
    currency,
    onOpenOrder,
    refreshDiningOptions,
}) {
    const { t, language, translationDetails } = useLanguage();

    const optionObj = diningOptionObjects?.[selectedDiningOption];
    const optionLabel =
        getEntityTranslation(optionObj, language, translationDetails) || translateDynamic(selectedDiningOption, t);

    const tableConfig = availableTableNumbers?.[selectedDiningOption];
    const isRandom = tableConfig === 'random_number';
    const isNumbered = Array.isArray(tableConfig);

    // Only this option's occupations feed status derivation + the reused
    // canvas (exact filter the legacy page applies, CashierSalesPage.js:2717).
    const optionOccupations = useMemo(
        () => (occupations || []).filter((o) => String(o.dining_option_id) === String(optionObj?.id)),
        [occupations, optionObj]
    );
    const { groupByAnchor, anchorByMember } = useMemo(
        () => buildTableGroups(optionOccupations),
        [optionOccupations]
    );

    // ONE selection handler for List tiles and the reused Visual canvas —
    // faithful to the legacy list-tile click (2749-2762): resolve merged
    // members to the anchor (the shared order lives under the anchor's key),
    // open an existing pending order, otherwise select the table.
    const handleTableTap = (num) => {
        const numStr = String(num);
        const occ = resolveOccupation(optionOccupations, numStr);
        const targetNum = occ ? String(occ.table_number) : numStr;
        const targetKey = `${selectedDiningOption}-${targetNum}`;
        if (pendingOrders?.[targetKey]) {
            onOpenOrder(targetKey, pendingOrders[targetKey]);
            return;
        }
        setTableNumber(targetNum);
    };

    const statusLabels = {
        available: t('available'),
        seated: t('seated'),
        ordered: t('ordered', {}, 'Ordered'),
        awaiting_bill: t('awaiting_bill'),
        paid: t('paid'),
    };

    // Tile meta line per mockup: order size + running total when an order
    // exists, otherwise the status timestamp (local time).
    const tileMeta = (status, occ, order) => {
        if (order && (status === 'ordered' || status === 'awaiting_bill')) {
            const count = (order.products || []).length;
            return `${count} ${t('items')} · ${currency}${(parseFloat(order.total) || 0).toFixed(2)}`;
        }
        if (status === 'seated') return `${t('seated')} ${fmtTime(occ?.seated_at)}`.trim();
        if (status === 'paid') return `${t('paid')} ${fmtTime(occ?.paid_at)}`.trim();
        if (status === 'awaiting_bill') return fmtTime(occ?.bill_requested_at);
        if (status === 'ordered') return fmtTime(occ?.first_order_at);
        return '';
    };

    // Random-number active orders (legacy 2641-2645).
    const activePending = useMemo(() => {
        if (!isRandom) return [];
        const prefix = `${selectedDiningOption}-`;
        return Object.entries(pendingOrders || {})
            .filter(([key]) => key.startsWith(prefix))
            .map(([key, order]) => ({ key, number: key.slice(prefix.length), order }));
    }, [isRandom, selectedDiningOption, pendingOrders]);

    return (
        <div className="tsl-root">
            <style>{CSS}</style>

            {/* header: title+sub | List|Visual toggle | 5-status legend */}
            <div className="tsl-head">
                <div>
                    <div className="tsl-title">{t('select_table')}</div>
                    <div className="tsl-sub">
                        {optionLabel} {t('requires_table_before_products', {}, 'requires a table before adding products')}
                    </div>
                </div>
                {isNumbered && (
                    <div className="tsl-seg" role="tablist" aria-label={t('table', {}, 'Table')}>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={viewMode !== 'visual'}
                            className={viewMode !== 'visual' ? 'active' : ''}
                            onClick={() => setViewMode('numbered')}
                        >
                            {ListIcon}
                            {t('list')}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={viewMode === 'visual'}
                            className={viewMode === 'visual' ? 'active' : ''}
                            onClick={() => setViewMode('visual')}
                        >
                            {GridIcon}
                            {t('visual')}
                        </button>
                    </div>
                )}
                {isNumbered && (
                    <div className="tsl-legend" aria-hidden="true">
                        {Object.keys(STATUS_TOKENS).map((status) => (
                            <span key={status} className="tsl-leg" style={{ color: STATUS_TOKENS[status] }}>
                                <i />
                                {statusLabels[status]}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* random-number variant: entry strip + active-orders quick grid */}
            {isRandom && (
                <>
                    <div className="tsl-entry">
                        <span>{t('order_number')}</span>
                        <input
                            type="text"
                            value={tableNumber}
                            onChange={(e) => setTableNumber(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && tableNumber.trim()) setTableNumberConfirmed(true);
                            }}
                            aria-label={`${t('enter')} ${optionLabel} ${t('number')}`}
                            autoFocus
                        />
                        <button
                            type="button"
                            className="tsl-confirm"
                            disabled={!tableNumber.trim()}
                            onClick={() => setTableNumberConfirmed(true)}
                        >
                            {t('confirm')}
                        </button>
                    </div>
                    {activePending.length > 0 && (
                        <>
                            <div className="tsl-active-h">
                                {t('active_orders', {}, 'Active Orders')} ({activePending.length})
                            </div>
                            <div className="tsl-active-grid">
                                {activePending.map(({ key, number, order }) => (
                                    <button
                                        key={key}
                                        type="button"
                                        className="tsl-active-tile"
                                        data-order-key={key}
                                        onClick={() => onOpenOrder(key, order)}
                                    >
                                        #{number}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </>
            )}

            {/* numbered list grid */}
            {isNumbered && viewMode !== 'visual' && (
                <div className="tsl-grid">
                    {tableConfig.length === 0 && (
                        <div className="tsl-none">{t('no_visual_layout_configured')} {optionLabel}</div>
                    )}
                    {tableConfig.map((num) => {
                        const numStr = String(num);
                        const tableKey = `${selectedDiningOption}-${numStr}`;
                        const occ = resolveOccupation(optionOccupations, numStr);
                        const status = deriveTableStatus(occ, !!pendingOrders?.[tableKey]);
                        const color = STATUS_TOKENS[status];
                        // Merged-group badges: "Group · N" on the anchor, an
                        // anchor pointer on members (via buildTableGroups).
                        const groupHere = groupByAnchor[numStr];
                        const memberOf = anchorByMember[numStr];
                        const badge = groupHere
                            ? `${t('group')} · ${occ?.party_size || groupHere.members.length + 1}`
                            : (memberOf ? `↳ ${memberOf.table_number}` : null);
                        const anchorKey = occ ? `${selectedDiningOption}-${occ.table_number}` : tableKey;
                        const order = pendingOrders?.[anchorKey] || pendingOrders?.[tableKey];
                        const meta = tileMeta(status, occ, order);
                        return (
                            <button
                                key={numStr}
                                type="button"
                                className={`tsl-tile${status === 'available' ? '' : ' busy'}`}
                                data-table={numStr}
                                data-status={status}
                                style={{ '--tt': color }}
                                onClick={() => handleTableTap(numStr)}
                            >
                                {badge && <span className="tsl-merged">{badge}</span>}
                                <span className="tsl-num">{typeof num === 'string' ? num : `T${num}`}</span>
                                <span>
                                    <span className="tsl-stat" style={{ color }}>
                                        <i />
                                        {statusLabels[status]}
                                    </span>
                                    {meta && <span className="tsl-meta">{meta}</span>}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* visual variant: REUSED VisualTableSelector, zero edits.
                hideHeader mode fills the slot and scales the 800×500 canvas
                to fit (no page scroll) and delegates every click back to
                onTableSelect — resolution runs through handleTableTap so
                both views behave identically. */}
            {isNumbered && viewMode === 'visual' && (
                <div className="tsl-canvas">
                    <VisualTableSelector
                        selectedDiningOption={selectedDiningOption}
                        selectedTable={tableNumber}
                        onTableSelect={(num) => { if (num) handleTableTap(num); }}
                        pendingOrders={pendingOrders}
                        printedOrders={printedOrders}
                        diningOptions={fullDiningOptions}
                        onDataChange={refreshDiningOptions}
                        isVisible={viewMode === 'visual'}
                        currency={currency}
                        occupations={optionOccupations}
                        hideHeader
                    />
                </div>
            )}
        </div>
    );
}
